/**
 * Row adapters: ListsWithStatsRow, GatewaysWithStatsRow, etc. → GenericStatsRow
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  ListsWithStatsRow,
  GatewaysWithStatsRow,
  PathsWithStatsRow,
  LandersWithStatsRow,
  OffersWithStatsRow,
  PhonesWithStatsRow,
  SourcesWithStatsRow,
  TextsWithStatsRow,
} from '@/features/smswiser-campaigns/types/workspace.types';
import type { GenericStatsRow } from '../../shared/generic-stats.types';
import { aggregateVoluum } from '../../shared';

export function toGenericStatsRowFromList(row: ListsWithStatsRow, index?: number): GenericStatsRow {
  const searchText = [row.listName, row.listId].join(' ').toLowerCase();
  const id = row.listId?.trim() || `list-empty-${index ?? 0}`;
  return {
    id,
    label: row.listName || row.listId || '—',
    subLabel: row.listId ? `ID: ${row.listId}` : undefined,
    voluum: row.voluum,
    leadsList: row.listName,
    searchText,
    _originalList: row,
  };
}

export function toGenericStatsRowFromGateway(row: GatewaysWithStatsRow, index?: number): GenericStatsRow {
  const searchText = [row.gatewayName, row.gatewayId].join(' ').toLowerCase();
  const id = row.gatewayId?.trim() || `gateway-empty-${index ?? 0}`;
  return {
    id,
    label: row.gatewayName || row.gatewayId || '—',
    subLabel: row.gatewayId ? `ID: ${row.gatewayId}` : undefined,
    voluum: row.voluum,
    gateway: row.gatewayName,
    searchText,
    _originalGateway: row,
  };
}

const EMPTY_PATH_PLACEHOLDERS = new Set(['', 'no value', 'n/a']);

export function toGenericStatsRowFromPath(row: PathsWithStatsRow, index?: number): GenericStatsRow {
  const searchText = [row.pathName, row.pathId].join(' ').toLowerCase();
  const rawId = row.pathId?.trim() ?? '';
  const useIndexFallback =
    !rawId || EMPTY_PATH_PLACEHOLDERS.has(rawId.toLowerCase());
  const id = useIndexFallback ? `path-empty-${index ?? 0}` : rawId;
  return {
    id,
    label: row.pathName || row.pathId || '—',
    subLabel: row.pathId ? `ID: ${row.pathId}` : undefined,
    voluum: row.voluum,
    searchText,
    _originalPath: row,
  };
}

export function toGenericStatsRowFromLander(row: LandersWithStatsRow, index?: number): GenericStatsRow {
  const searchText = [row.landerName, row.landerId].join(' ').toLowerCase();
  const id = row.landerId?.trim() || `lander-empty-${index ?? 0}`;
  return {
    id,
    label: row.landerName || row.landerId || '—',
    subLabel: row.landerId ? `ID: ${row.landerId}` : undefined,
    voluum: row.voluum,
    searchText,
    _originalLander: row,
  };
}

export function toGenericStatsRowFromOffer(row: OffersWithStatsRow, index?: number): GenericStatsRow {
  const searchText = [row.offerName, row.offerId].join(' ').toLowerCase();
  const id = row.offerId?.trim() || `offer-empty-${index ?? 0}`;
  return {
    id,
    label: row.offerName || row.offerId || '—',
    subLabel: row.offerId ? `ID: ${row.offerId}` : undefined,
    voluum: row.voluum,
    searchText,
    _originalOffer: row,
  };
}

export function toGenericStatsRowFromSource(row: SourcesWithStatsRow, index?: number): GenericStatsRow {
  const searchText = [row.sourceName, row.sourceId].join(' ').toLowerCase();
  const id = row.sourceId?.trim() || `source-empty-${index ?? 0}`;
  return {
    id,
    label: row.sourceName || row.sourceId || '—',
    subLabel: row.sourceId ? `ID: ${row.sourceId}` : undefined,
    voluum: row.voluum,
    searchText,
    _originalSource: row,
  };
}

export function toGenericStatsRowFromPhone(row: PhonesWithStatsRow, index?: number): GenericStatsRow {
  const searchText = [row.phone, row.sourceId].join(' ').toLowerCase();
  const id = `${row.phone}::${row.sourceId}`.trim() || `phone-empty-${index ?? 0}`;
  const voluum = row.voluum ?? row.metrics ?? null;
  return {
    id,
    label: row.phone || '—',
    subLabel: row.sourceId || undefined,
    voluum,
    searchText,
    _originalPhone: row,
  };
}

function resolveAiTextGroupLabel(row: TextsWithStatsRow): string {
  if (row.aiTextRoleId === 'master') return 'Master prompt';
  const groupName = row.aiTextGroupName?.trim();
  if (groupName) return groupName;
  if (row.aiTextRoleId?.trim() || row.aiTextRoleName?.trim()) return 'Ungrouped text prompt';
  return 'Unknown text group';
}

function resolveAiTextRoleLabel(row: TextsWithStatsRow): string {
  const roleName = row.aiTextRoleName?.trim();
  if (roleName) return roleName;
  if (row.aiTextRoleId === 'master') return 'Master prompt';
  const roleId = row.aiTextRoleId?.trim();
  return roleId || 'Unknown text prompt';
}

export function toGenericStatsRowFromText(row: TextsWithStatsRow): GenericStatsRow {
  const isGatewayRow = row.gatewayId != null;
  const id = isGatewayRow
    ? `${row.gatewayId}::${row.textId}`
    : `${row.dropId ?? ''}::${row.textId}`;
  const searchParts = [row.textName, row.textId];
  const aiTextGroup = resolveAiTextGroupLabel(row);
  const aiTextRole = resolveAiTextRoleLabel(row);
  searchParts.push(aiTextGroup, aiTextRole);
  if (isGatewayRow && row.gatewayName) searchParts.push(row.gatewayName);
  else if (row.dropId) searchParts.push(row.dropId);
  const searchText = searchParts.join(' ').toLowerCase();

  return {
    id,
    label: row.textName || row.textId || '—',
    subLabel: row.textId ? `ID: ${row.textId}` : undefined,
    voluum: row.voluum,
    dropId: row.dropId,
    gateway: row.gatewayName ?? (row.gatewayId ? `Gateway ${row.gatewayId}` : undefined),
    aiTextGroup,
    aiTextRole,
    searchText,
    dropCount: isGatewayRow ? 0 : 1,
    _originalText: row,
  };
}

/** Merge text rows by template ID (textId). */
export function mergeTextRowsById(rows: GenericStatsRow[]): GenericStatsRow[] {
  const byTextId = new Map<string, GenericStatsRow[]>();
  for (const r of rows) {
    const textId = r._originalText?.textId ?? r.id.split('::')[1] ?? r.id;
    const list = byTextId.get(textId) ?? [];
    list.push(r);
    byTextId.set(textId, list);
  }
  return Array.from(byTextId.entries()).map(([textId, groupRows]) => {
    const first = groupRows[0]!;
    const textLabel = first.label ?? first._originalText?.textName ?? textId ?? '—';
    const orig = first._originalText;
    return {
      id: textId,
      label: textLabel,
      subLabel: `ID: ${textId}`,
      voluum: aggregateVoluum(groupRows),
      searchText: [textLabel, textId].join(' ').toLowerCase(),
      dropCount: groupRows.length,
      _originalText: {
        dropId: orig?.dropId ?? '',
        textId,
        textName: textLabel,
        voluum: aggregateVoluum(groupRows),
      },
    };
  });
}
